// frontend/src/pages/Usuarios.js
import React, { useEffect, useState } from "react";
import "./usuarios.css";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import ThemeProvider from "../theme/TemaMuiProvider";
import { FaEye, FaEyeSlash, FaCopy, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { Box, Button, TablePagination, TextField, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import muiTheme from "../theme/muiTheme";

function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [usuariosPorPagina, setUsuariosPorPagina] = useState(5);

  // olho mágico (senha)
  const [mostrarSenha, setMostrarSenha] = useState(false);

  // ✅ minimizar/expandir grid (persistido)
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("usuariosGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("usuariosGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  // SISTEMA DE PERMISSÕES
  const token = localStorage.getItem("token");
  let tipoUsuario = "";

  if (token) {
    const decoded = jwtDecode(token);
    tipoUsuario = decoded.tipo
      ?.toString()
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  }

  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeEditar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeExcluir = ["gestor", "administrador"].includes(tipoUsuario);
  const podeInutilizar = ["gestor", "administrador"].includes(tipoUsuario);

  const perfilUsuario = tipoUsuario;

  // Paginação
  const indexInicial = (paginaAtual - 1) * usuariosPorPagina;
  const indexFinal = indexInicial + usuariosPorPagina;

  const usuariosFiltrados = usuarios.filter((u) => `${u.nome} ${u.email}`.toLowerCase().includes(filtro.toLowerCase()));

  const usuariosPaginados = usuariosFiltrados.slice(indexInicial, indexFinal);
  const totalPaginas = Math.ceil(usuariosFiltrados.length / usuariosPorPagina);

  const [novoUsuario, setNovoUsuario] = useState({
    id: null,
    nome: "",
    email: "",
    senha: "",
    perfil: "Visitante",
    matriculaPermitida: false,
  });

  const muiCampoSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 56,
      display: "flex",
      alignItems: "center",

      "& fieldset": {
        borderColor: "var(--ig-b-555555)",
      },

      "&:hover fieldset": {
        borderColor: "var(--ig-b-777777)",
      },

      "&.Mui-focused fieldset": {
        borderColor: "#2ea8ff",
        borderWidth: 2,
      },
    },

    "& .MuiOutlinedInput-input": {
      padding: "16.5px 14px",
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root": {
      color: "var(--ig-t-ffffff)",
    },

    "& .MuiInputLabel-root.Mui-focused": {
      color: "#2ea8ff",
    },

    "& .MuiSvgIcon-root": {
      color: "var(--ig-t-ffffff)",
    },
  };

  const menuPropsDark = {
    MenuProps: {
      PaperProps: {
        sx: {
          backgroundColor: "var(--ig-f-111827) !important",
          color: "var(--ig-t-ffffff)",
        },
      },
    },
  };

  const carregarUsuarios = async () => {
    const token = localStorage.getItem("token");

    try {
      setLoading(true);

      const res = await fetch("/api/usuarios", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.erro || "Erro ao buscar usuários");
      }

      setUsuarios(Array.isArray(data) ? data : []);
      setErro(null);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Buscar usuários
  useEffect(() => {
    if (!podeVisualizar) {
      setLoading(false);
      return;
    }

    carregarUsuarios();
  }, [podeVisualizar]);

  // Salvar ou editar usuário
  const handleCadastro = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    const metodo = novoUsuario.id ? "PUT" : "POST";
    const url = novoUsuario.id ? `/api/usuarios/${novoUsuario.id}` : "/api/usuarios";

    try {
      const res = await fetch(url, {
        method: metodo,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(novoUsuario),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.erro || "Erro ao salvar usuário");

      alert("Usuário salvo com sucesso!");

      await carregarUsuarios();

      setNovoUsuario({
        id: null,
        nome: "",
        email: "",
        senha: "",
        perfil: "Visitante",
        matriculaPermitida: false,
      });

      setMostrarSenha(false);
    } catch (err) {
      alert(err.message);
    }
  };

  // Editar
  const handleEditar = (usuario) => {
    setNovoUsuario({
      id: usuario.id,
      nome: usuario.nome,
      email: usuario.email,
      senha: "",
      perfil: usuario.tipo || usuario.perfil || "Visitante",
      matriculaPermitida: !!usuario.matriculaPermitida,
    });

    setMostrarSenha(false);

    document.querySelector(".card-form")?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // Inutilizar
  const handleDesativar = async (id) => {
    if (!window.confirm("Deseja realmente inutilizar este usuário?")) return;

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/usuarios/${id}/desativar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.erro || "Erro ao inutilizar usuário");

      await carregarUsuarios();
      alert("Usuário inutilizado com sucesso!");
    } catch (err) {
      alert(err.message);
    }
  };

  // ATIVAR USUÁRIO
  const handleAtivar = async (id) => {
    if (!window.confirm("Deseja ativar este usuário novamente?")) return;

    const token = localStorage.getItem("token");

    try {
      const res = await fetch(`/api/usuarios/${id}/ativar`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.erro || "Erro ao ativar usuário");

      await carregarUsuarios();
      alert("Usuário ativado com sucesso!");
    } catch (err) {
      alert(err.message);
    }
  };

  // Copiar lista
  const copiarTabela = async () => {
    const texto = usuariosFiltrados.map((u) => `${u.nome} <${u.email}> <${u.tipo}>`).join("\n");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);
    alert(ok ? "Usuários copiados!" : "Não foi possível copiar.");
  };

  // CSV
  const handleCSV = () => {
    const BOM = "\uFEFF";
    const header = ["Nome", "Email", "Tipo"];

    const csvEscape = (value) => {
      const s = String(value ?? "");
      if (/[;"\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const linhas = (usuariosFiltrados || []).map((u) => [u.nome ?? "", u.email ?? "", u.tipo ?? ""].map(csvEscape));

    const csv = [header.map(csvEscape), ...linhas].map((l) => l.join(";")).join("\n");

    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "usuarios.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  // Sem permissão
  if (!podeVisualizar) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className="usuarios-page">
          <h2 className="page-title">Usuários</h2>

          <div className="aviso-permissao-container">
            <p className="aviso-permissao">🔒 Você não tem permissão para acessar esta área.</p>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="usuarios-page">
        <style>{`
        .campo-formulario-padrao,
        .campo-formulario-padrao:focus,
        .campo-formulario-padrao:active,
        .campo-formulario-padrao:read-only,
        .campo-formulario-padrao:disabled {
          background: transparent !important;
          background-color: transparent !important;
          color: var(--ig-t-ffffff) !important;
          box-shadow: none !important;
          -webkit-text-fill-color: var(--ig-t-ffffff) !important;
          opacity: 1 !important;
        }

        .campo-formulario-padrao::placeholder {
          color: var(--ig-t-255-255-255-a072) !important;
          -webkit-text-fill-color: var(--ig-t-255-255-255-a072) !important;
        }

        .campo-formulario-padrao::-webkit-calendar-picker-indicator {
          filter: var(--ig-x-11pexw5);
          opacity: 0.9;
          cursor: pointer;
        }
      `}</style>

        <h2 className="page-title">Usuários</h2>

        {/* GRID / LISTA */}
        <div className="card card-table">
          {/* ✅ Filtros e botões (SEMPRE VISÍVEIS) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                height: 52,
                width: "100%",
                border: "1px solid var(--ig-b-555555)",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                backgroundColor: "transparent",
                boxSizing: "border-box",
                px: 2,
              }}
            >
              <input
                type="text"
                placeholder="Buscar Usuário por nome ou e-mail"
                value={filtro}
                onChange={(e) => {
                  setFiltro(e.target.value);
                  setPaginaAtual(1);
                }}
                style={{
                  flex: 1,
                  height: "100%",
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  color: "var(--ig-t-ffffff)",
                  fontSize: "1rem",
                }}
              />

              <SearchIcon
                sx={{
                  color: "var(--ig-t-9ca3af)",
                  fontSize: 20,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                type="button"
                onClick={() => {
                  setFiltro("");
                  setPaginaAtual(1);
                }}
                sx={{
                  height: 52,
                  color: "#2196f3",
                  borderColor: "#2196f3",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#42a5f5",
                    backgroundColor: "rgba(33, 150, 243, 0.08)",
                  },
                }}
              >
                Limpar Filtro
              </Button>

              <Button
                size="small"
                variant="contained"
                type="button"
                onClick={copiarTabela}
                sx={{
                  height: 52,
                  backgroundColor: "#2196f3",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                <FaCopy style={{ marginRight: 6 }} />
                Copiar
              </Button>

              <Button
                size="small"
                variant="contained"
                type="button"
                onClick={handleCSV}
                sx={{
                  height: 52,
                  backgroundColor: "#2196f3",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                  },
                }}
              >
                CSV
              </Button>
            </Box>
          </Box>

          {/* ✅ Botão minimizar/expandir (abaixo dos botões e alinhado à direita) */}
          <div className="grid-toggle-area right">
            <button
              type="button"
              className={`btn-grid-toggle small ${gridMinimizado ? "outline" : "danger"}`}
              onClick={() => setGridMinimizado((v) => !v)}
              title={gridMinimizado ? "Expandir o Grid" : "Minimizar o Grid"}
              aria-label={gridMinimizado ? "Expandir o Grid" : "Minimizar o Grid"}
            >
              {gridMinimizado ? (
                <>
                  <FaChevronUp />
                  Expandir
                </>
              ) : (
                <>
                  <FaChevronDown />
                  Minimizar
                </>
              )}
            </button>
          </div>

          {/* ✅ Somente a TABELA some ao minimizar */}
          {!gridMinimizado && (
            <div className="table-wrap">
              {loading ? (
                <div className="empty">Carregando usuários...</div>
              ) : (
                <>
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>Ações</th>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Status</th>
                        <th>Tipo Usuário</th>
                      </tr>
                    </thead>

                    <tbody>
                      {usuariosPaginados.map((usuario) => (
                        <tr key={usuario.id}>
                          <td className="actions-cell">
                            {podeEditar && (
                              <button className="small-btn edit" type="button" onClick={() => handleEditar(usuario)}>
                                <i className="fas fa-edit"></i>
                                Editar
                              </button>
                            )}

                            {podeInutilizar &&
                              (usuario.ativo ? (
                                <button className="small-btn disable" type="button" onClick={() => handleDesativar(usuario.id)}>
                                  <i className="fas fa-ban"></i>
                                  Inutilizar
                                </button>
                              ) : (
                                <button className="small-btn enable" type="button" onClick={() => handleAtivar(usuario.id)}>
                                  <i className="fas fa-check"></i> Ativar
                                </button>
                              ))}
                          </td>

                          <td>{usuario.nome}</td>
                          <td>{usuario.email}</td>
                          <td>
                            <span className={`status-badge ${usuario.ativo ? "ativo" : "inativo"}`}>{usuario.ativo ? "Ativo" : "Inativo"}</span>
                          </td>
                          <td>{usuario.tipo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Rodapé */}
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                      pt: 1,
                    }}
                  >
                    <TablePagination
                      component="div"
                      count={usuariosFiltrados.length}
                      page={paginaAtual - 1}
                      onPageChange={(_, novaPagina) => setPaginaAtual(novaPagina + 1)}
                      rowsPerPage={usuariosPorPagina}
                      onRowsPerPageChange={(e) => {
                        setUsuariosPorPagina(parseInt(e.target.value, 10));
                        setPaginaAtual(1);
                      }}
                      rowsPerPageOptions={[5, 10, 25]}
                      labelRowsPerPage="Usuários por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                      slotProps={{
                        select: {
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                backgroundColor: "var(--ig-f-111827)",
                                color: "var(--ig-t-ffffff)",

                                "& .MuiMenuItem-root": {
                                  color: "var(--ig-t-ffffff)",
                                },

                                "& .MuiMenuItem-root:hover": {
                                  backgroundColor: "var(--ig-f-1f2937)",
                                },

                                "& .MuiMenuItem-root.Mui-selected": {
                                  backgroundColor: "var(--ig-f-1f2937)",
                                },
                              },
                            },
                          },
                        },
                      }}
                      sx={{
                        color: "var(--ig-t-ffffff)",

                        "& .MuiToolbar-root": {
                          minHeight: 42,
                          paddingLeft: 0,
                          paddingRight: 0,
                        },

                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                          fontSize: "0.8rem",
                        },

                        "& .MuiTablePagination-selectIcon": {
                          color: "var(--ig-t-ffffff)",
                        },

                        "& .MuiTablePagination-actions button": {
                          color: "var(--ig-t-ffffff)",
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </div>
          )}
        </div>

        {/* FORMULÁRIO */}
        {(perfilUsuario === "gestor" || perfilUsuario === "administrador") && (
          <div className="card card-form">
            <form onSubmit={handleCadastro} className="form-grid form-dark" autoComplete="off">
              <TextField
                label="Nome"
                value={novoUsuario.nome}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, nome: e.target.value })}
                required
                fullWidth
                size="small"
                sx={muiCampoSx}
              />

              <TextField
                label="E-mail"
                type="text"
                name="usuario_email_cadastro"
                autoComplete="off"
                value={novoUsuario.email}
                onChange={(e) => setNovoUsuario({ ...novoUsuario, email: e.target.value })}
                required
                fullWidth
                size="small"
                sx={muiCampoSx}
              />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                <TextField
                  label="Senha"
                  type={mostrarSenha ? "text" : "password"}
                  name="usuario_senha_cadastro"
                  autoComplete="new-password"
                  value={novoUsuario.senha}
                  onChange={(e) => setNovoUsuario({ ...novoUsuario, senha: e.target.value })}
                  required={!novoUsuario.id}
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  InputProps={{
                    endAdornment: (
                      <button type="button" className="toggle-senha-mui" onClick={() => setMostrarSenha((v) => !v)}>
                        {mostrarSenha ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    ),
                  }}
                />

                <TextField
                  select
                  label="Tipo de Usuário"
                  value={novoUsuario.perfil || ""}
                  onChange={(e) =>
                    setNovoUsuario((prev) => ({
                      ...prev,
                      perfil: e.target.value,
                    }))
                  }
                  fullWidth
                  size="small"
                  sx={muiCampoSx}
                  SelectProps={menuPropsDark}
                >
                  <MenuItem value="Visitante">Visitante</MenuItem>
                  <MenuItem value="Usuário Padrão">Usuário Padrão</MenuItem>
                  <MenuItem value="Gestor">Gestor</MenuItem>
                  <MenuItem value="Administrador">Administrador</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                  type="checkbox"
                  checked={novoUsuario.matriculaPermitida}
                  onChange={(e) =>
                    setNovoUsuario({
                      ...novoUsuario,
                      matriculaPermitida: e.target.checked,
                    })
                  }
                />
                <span>Permissão para Matrículas</span>
              </Box>

              <Box className="form-botoes-usuarios">
                <button type="submit" className="iptu-btn-acao btn-cadastrar">
                  <i className="fas fa-save"></i>

                  <span>{novoUsuario.id ? "Atualizar" : "Cadastrar"}</span>
                </button>

                <button
                  type="button"
                  className="iptu-btn-acao btn-acao"
                  onClick={() => {
                    setNovoUsuario({
                      id: null,
                      nome: "",
                      email: "",
                      senha: "",
                      perfil: "Visitante",
                      matriculaPermitida: false,
                    });
                    setMostrarSenha(false);
                  }}
                >
                  <i className="fas fa-times"></i>
                  <span>Cancelar</span>
                </button>
              </Box>
            </form>
          </div>
        )}

        {erro && (
          <div className="aviso-permissao-container">
            <p className="aviso-permissao">⚠️ {erro}</p>
          </div>
        )}
      </div>
    </ThemeProvider>
  );
}

export default Usuarios;
